/* RESPONSIBLE TEAM: team-self-serve */

import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency-decorators';
import ajax from 'embercom/lib/ajax';
import moment from 'moment-timezone';
import type UsageLimitsService from './usage-limits-service';
import type ApplicationInstance from '@ember/application/instance';
import { taskFor } from 'ember-concurrency-ts';

export default class FinUsageService extends Service {
  @service declare customerService: $TSFixMe;
  @service declare appService: $TSFixMe;
  @service declare usageLimitsService: UsageLimitsService;

  @tracked finResolutionsInCurrentCycle = 0;

  constructor(owner: ApplicationInstance) {
    super(owner);

    taskFor(this.getFinResolutionsInCurrentCycle).perform();
    taskFor(this.fetchFinUsageLimits).perform();
  }

  get subscription() {
    return this.customerService.customer.subscription;
  }

  get daysLeftInBillingPeriod() {
    let today = moment().startOf('day');
    let cycleEndDate = moment(this.subscription?.nextMonthlyInvoiceDate).startOf('day');
    let diff = cycleEndDate.diff(today);
    return Math.floor(moment.duration(diff).asDays());
  }

  get isInTrial() {
    return !!this.customerService.customer?.inTrial;
  }

  get isFinUsageLimitEnabled() {
    return this.usageLimitsService.finUsageLimitEnabled;
  }

  get finResolutionsLimit() {
    if (!this.isFinUsageLimitEnabled) {
      return 0;
    }

    return this.usageLimitsService.finUsageLimitThreshold;
  }

  get isFinResolutionsLimitValid() {
    return this.finResolutionsLimit > 0;
  }

  get areUsedResolutions90PercentOfLimit() {
    if (!this.isFinResolutionsLimitValid || this.isFinResolutionsLimitReached) {
      return false;
    }
    return this.finResolutionsInCurrentCycle >= this.finResolutionsLimit * 0.9;
  }

  get isFinResolutionsLimitReached() {
    if (!this.isFinResolutionsLimitValid) {
      return false;
    }
    return this.finResolutionsInCurrentCycle >= this.finResolutionsLimit;
  }

  @restartableTask
  *getFinResolutionsInCurrentCycle() {
    try {
      let usage: { lastObject: { resolutions_with_custom_answers: number } } = yield ajax({
        url: '/ember/pricing_model_daily_app_stats/fetch',
        type: 'GET',
        data: { app_id: this.appService.app.id, current_period: true },
      });

      this.finResolutionsInCurrentCycle = usage.lastObject.resolutions_with_custom_answers;
    } catch (error) {
      this.finResolutionsInCurrentCycle = -1;
    }
  }

  @restartableTask
  *fetchFinUsageLimits() {
    yield this.usageLimitsService.fetchFinUsageLimits();
  }
}
